import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

import "./About.css";

import ceoImage from "../assets/ceo.jpg";
import sameerImage from "../assets/sameer.jpeg";
import razaImage from "../assets/raza.jpeg";
import logo from "../assets/SamsoftixLogo.jpg";

function About() {
  const navigate = useNavigate();

  const handleInternsClick = () => {
    navigate("/interns");
  };

  const handleContactClick = () => {
    navigate("/contact");
  };

  /*
   * =========================================================
   * LEADERSHIP TEAM
   * =========================================================
   */

  const leadershipTeam = [
    {
      name: "Qasim Mehmood Cheema",
      role: "Founder & CEO",
      image: ceoImage,
      alt: "Qasim Mehmood Cheema - Founder and CEO of SamSoftix",
      description:
        "Qasim Mehmood Cheema is the Founder and CEO of SamSoftix. He leads the company's overall vision, business strategy, client relationships, technology initiatives, and training programs.",
      details:
        "Under his leadership, SamSoftix focuses on delivering professional web, mobile, AI, ecommerce, game, and custom software solutions for startups, businesses, organizations, and international clients.",
      featured: true,
    },

    {
      name: "Sameer",
      role: "Chief Operating Officer (COO)",
      image: sameerImage,
      alt: "Sameer - Chief Operating Officer at SamSoftix",
      description:
        "Sameer serves as the Chief Operating Officer (COO) at SamSoftix, supporting day-to-day operations, business processes, team coordination, and organizational growth.",
      details:
        "His role focuses on operational efficiency, coordination between teams, project support, and helping SamSoftix maintain a professional experience for clients, partners, students, and interns.",
      featured: false,
    },

    {
      name: "Raza Ali Anjum",
      role: "Chief Technology Officer (CTO)",
      image: razaImage,
      alt: "Raza Ali Anjum - Chief Technology Officer at SamSoftix",
      description:
        "Raza Ali Anjum serves as the Chief Technology Officer (CTO) at SamSoftix, focusing on technology strategy, software architecture, development practices, and technical innovation.",
      details:
        "He works with the technical team on scalable, secure, and performance-focused digital products across web development, mobile applications, AI solutions, ecommerce platforms, games, and custom software.",
      featured: false,
    },
  ];

  /*
   * =========================================================
   * STRUCTURED DATA
   * =========================================================
   */

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://www.samsoftix.com/#organization",
        name: "SamSoftix",
        url: "https://www.samsoftix.com/",
        logo: "https://www.samsoftix.com/assets/SamsoftixLogo.jpg",
        description:
          "SamSoftix is a software house and technology training center based in Wan and Sambrial, Punjab, Pakistan, providing web development, mobile app development, AI solutions, ecommerce, game development, custom software, SEO and technology training.",
        founder: {
          "@type": "Person",
          name: "Qasim Mehmood Cheema",
          jobTitle: "Founder & CEO",
        },
        employee: [
          {
            "@type": "Person",
            name: "Qasim Mehmood Cheema",
            jobTitle: "Founder & CEO",
          },
          {
            "@type": "Person",
            name: "Sameer",
            jobTitle: "Chief Operating Officer (COO)",
          },
          {
            "@type": "Person",
            name: "Raza Ali Anjum",
            jobTitle: "Chief Technology Officer (CTO)",
          },
        ],
        areaServed: [
          {
            "@type": "City",
            name: "Sambrial",
          },
          {
            "@type": "City",
            name: "Sialkot",
          },
          {
            "@type": "AdministrativeArea",
            name: "Punjab",
          },
          {
            "@type": "Country",
            name: "Pakistan",
          },
        ],
        sameAs: [
          "https://www.facebook.com/samsoftix",
          "https://www.linkedin.com/company/samsoftix",
          "https://www.instagram.com/samsoftix",
        ],
      },

      /*
       * Sambrial Local Business
       */

      {
        "@type": "LocalBusiness",
        "@id": "https://www.samsoftix.com/#sambrial-office",
        name: "SamSoftix",
        url: "https://www.samsoftix.com/",
        image:
          "https://www.samsoftix.com/assets/SamsoftixLogo.jpg",
        telephone: "+923320472184",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Moon Plaza",
          addressLocality: "Sambrial",
          addressRegion: "Punjab",
          postalCode: "51000",
          addressCountry: "PK",
        },
      },

      /*
       * Wan Local Business
       */

      {
        "@type": "LocalBusiness",
        "@id": "https://www.samsoftix.com/#wan-office",
        name: "SamSoftix",
        url: "https://www.samsoftix.com/",
        image:
          "https://www.samsoftix.com/assets/SamsoftixLogo.jpg",
        telephone: "+923320472184",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Wan",
          addressLocality: "Sambrial",
          addressRegion: "Punjab",
          postalCode: "51010",
          addressCountry: "PK",
        },
      },

      /*
       * Breadcrumb
       */

      {
        "@type": "BreadcrumbList",
        "@id":
          "https://www.samsoftix.com/about#breadcrumb",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.samsoftix.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "About SamSoftix",
            item: "https://www.samsoftix.com/about",
          },
        ],
      },

      /*
       * WebPage
       */

      {
        "@type": "AboutPage",
        "@id": "https://www.samsoftix.com/about#aboutpage",
        url: "https://www.samsoftix.com/about",
        name: "About SamSoftix",
        description:
          "Learn about SamSoftix, its leadership team, software development services, technology training programs, and locations in Wan and Sambrial, Punjab, Pakistan.",
        isPartOf: {
          "@id": "https://www.samsoftix.com/#organization",
        },
        about: {
          "@id": "https://www.samsoftix.com/#organization",
        },
      },
    ],
  };

  return (
    <div className="about-page">

      {/* =====================================================
          SEO / META DATA
      ====================================================== */}

      <Helmet>

        <title>
          About SamSoftix | Software House in Sambrial, Sialkot, Pakistan
        </title>

        <meta
          name="description"
          content="Learn about SamSoftix, a software house and technology training center in Wan and Sambrial near Sialkot, Pakistan. Meet our Founder & CEO Qasim Mehmood Cheema, COO Sameer, and CTO Raza Ali Anjum."
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large"
        />

        <link
          rel="canonical"
          href="https://www.samsoftix.com/about"
        />

        {/* =================================================
            OPEN GRAPH
        ================================================== */}

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:title"
          content="About SamSoftix | Software House in Pakistan"
        />

        <meta
          property="og:description"
          content="Meet the leadership team behind SamSoftix, a software house and technology training company based in Wan and Sambrial near Sialkot, Pakistan."
        />

        <meta
          property="og:url"
          content="https://www.samsoftix.com/about"
        />

        <meta
          property="og:site_name"
          content="SamSoftix"
        />

        <meta
          property="og:locale"
          content="en_PK"
        />

        <meta
          property="og:image"
          content="https://www.samsoftix.com/assets/SamsoftixLogo.jpg"
        />

        <meta
          property="og:image:alt"
          content="SamSoftix Software House Logo"
        />

        {/* =================================================
            TWITTER / X
        ================================================== */}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="About SamSoftix | Software House in Pakistan"
        />

        <meta
          name="twitter:description"
          content="SamSoftix is a software house and technology training center serving businesses in Pakistan and international clients."
        />

        <meta
          name="twitter:image"
          content="https://www.samsoftix.com/assets/SamsoftixLogo.jpg"
        />

        {/* =================================================
            STRUCTURED DATA
        ================================================== */}

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>

      </Helmet>


      {/* =====================================================
          HERO SECTION
      ====================================================== */}

      <section className="about-hero">

        <div className="about-hero-glow"></div>

        <img
          src={logo}
          alt="SamSoftix software house and technology company logo"
          className="about-logo"
          loading="eager"
        />

        <span className="about-eyebrow">
          SOFTWARE • AI • DIGITAL SOLUTIONS • TRAINING
        </span>

        <h1 className="about-title">
          About{" "}
          <span className="about-brand">
            SamSoftix
          </span>
        </h1>

        <h2>
          Software House & Technology Training Company in Pakistan
        </h2>

        <p>
          <strong>SamSoftix</strong> is a software house and
          technology training center based in{" "}
          <strong>
            Wan and Sambrial, Punjab, Pakistan
          </strong>
          . We develop modern digital solutions for startups,
          businesses, organizations, and international clients.
        </p>

        <p>
          Our services include{" "}
          <strong>
            website development, mobile app development,
            AI solutions, ecommerce development, game
            development, custom software, SEO, and digital
            solutions
          </strong>
          .
        </p>

        <p>
          We also provide{" "}
          <strong>
            practical technology training and internship
            programs
          </strong>
          , helping students and aspiring developers gain
          hands-on experience with real-world software
          projects.
        </p>

        {/* GEO / LOCATION SIGNALS */}

        <div className="about-location-pills">

          <span>
            📍 Wan
          </span>

          <span>
            📍 Sambrial
          </span>

          <span>
            📍 Sialkot Region
          </span>

          <span>
            🇵🇰 Pakistan
          </span>

          <span>
            🌎 International Clients
          </span>

        </div>

      </section>


      {/* =====================================================
          COMPANY VALUES
      ====================================================== */}

      <section className="about-values">

        {/* VISION */}

        <article className="value-card">

          <div className="value-icon">
            🚀
          </div>

          <h2>
            Our Vision
          </h2>

          <p>
            Our vision is to build SamSoftix into a trusted
            technology partner for businesses in Pakistan and
            international markets while contributing to the
            growth of the technology ecosystem through
            software innovation and practical digital
            education.
          </p>

        </article>


        {/* MISSION */}

        <article className="value-card">

          <div className="value-icon">
            💡
          </div>

          <h2>
            Our Mission
          </h2>

          <p>
            Our mission is to deliver professional{" "}
            <strong>
              web, mobile app, game, AI, ecommerce, and
              custom software solutions
            </strong>{" "}
            that help businesses establish, improve, and
            scale their digital presence.
          </p>

          <p>
            We also focus on practical education,
            mentorship, and real project experience to help
            learners develop industry-relevant technology
            skills.
          </p>

        </article>


        {/* TRAINING */}

        <article className="value-card">

          <div className="value-icon">
            🎓
          </div>

          <h2>
            Internships & Training
          </h2>

          <p>
            SamSoftix offers practical internships and
            training programs covering areas such as{" "}
            <strong>
              Web Development, Mobile App Development,
              Game Development, AI/ML, UI/UX, Graphic Design,
              Office Management, and Freelancing
            </strong>
            .
          </p>

          <button
            className="interns-btn"
            onClick={handleInternsClick}
          >
            Explore Internship Programs
          </button>

        </article>

      </section>


      {/* =====================================================
          LEADERSHIP SECTION
      ====================================================== */}

      <section
        className="leadership-section"
        aria-labelledby="leadership-heading"
      >

        <div className="leadership-heading">

          <span>
            LEADERSHIP AT SAMSOFTIX
          </span>

          <h2 id="leadership-heading">
            Meet the People Behind{" "}
            <span>
              SamSoftix
            </span>
          </h2>

          <p>
            SamSoftix is led by a team responsible for
            company strategy, operations, technology,
            software development, and client delivery.
          </p>

        </div>


        <div className="leadership-grid">

          {leadershipTeam.map((person) => (

            <article
              className={`leader-card ${
                person.featured
                  ? "leader-card-featured"
                  : ""
              }`}
              key={person.name}
            >

              {/* IMAGE */}

              <div className="leader-image-wrapper">

                <img
                  src={person.image}
                  alt={person.alt}
                  className="leader-image"
                  loading="lazy"
                />

                {person.featured && (
                  <span className="founder-badge">
                    FOUNDER
                  </span>
                )}

              </div>


              {/* CONTENT */}

              <div className="leader-content">

                <span className="leader-role">
                  {person.role}
                </span>

                <h3>
                  {person.name}
                </h3>

                <div className="leader-line"></div>

                <p>
                  {person.description}
                </p>

                <p>
                  {person.details}
                </p>

              </div>

            </article>

          ))}

        </div>

      </section>


      {/* =====================================================
          FOUNDER / CEO SPOTLIGHT
      ====================================================== */}

      <section
        className="ceo-section"
        aria-labelledby="founder-heading"
      >

        <div className="ceo-card">

          {/* CEO IMAGE */}

          <div className="ceo-image-wrapper">

            <img
              src={ceoImage}
              alt="Qasim Mehmood Cheema - Founder and CEO of SamSoftix"
              loading="lazy"
            />

            <span className="ceo-status">
              Founder & CEO
            </span>

          </div>


          {/* CEO INFORMATION */}

          <div className="ceo-info">

            <span className="ceo-label">
              FOUNDER & CEO
            </span>

            <h2 id="founder-heading">
              Qasim Mehmood Cheema
            </h2>

            <p className="ceo-location">
              📍 Head Office: Wan, Punjab, Pakistan
            </p>

            <p>
              <strong>
                Qasim Mehmood Cheema is the Founder and CEO
                of SamSoftix.
              </strong>{" "}
              He leads the company's vision and works across
              business strategy, technology, client
              relationships, software development initiatives,
              and technology education.
            </p>

            <p>
              SamSoftix works with startups, SMEs,
              organizations, and international clients to
              develop modern digital solutions including
              websites, mobile applications, AI systems,
              ecommerce platforms, games, and custom
              software.
            </p>

            <p>
              Qasim also supports the company's training and
              internship programs, helping students and
              aspiring developers gain practical experience
              through real-world technology projects.
            </p>


            {/* CEO EXPERTISE */}

            <div className="ceo-tags">

              <span>
                Software Development
              </span>

              <span>
                AI Solutions
              </span>

              <span>
                Business Strategy
              </span>

              <span>
                Technology Education
              </span>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          WHAT WE DO
      ====================================================== */}

      <section
        className="about-services"
        aria-labelledby="services-heading"
      >

        <div className="services-heading">

          <span>
            WHAT WE DO
          </span>

          <h2 id="services-heading">
            Technology Solutions for Modern Businesses
          </h2>

          <p>
            From business websites to custom software and
            AI-powered solutions, SamSoftix helps
            organizations build and improve their digital
            products.
          </p>

        </div>


        <div className="services-grid">

          {/* WEB */}

          <article className="service-mini-card">

            <span>
              🌐
            </span>

            <h3>
              Web Development
            </h3>

            <p>
              Modern, responsive, SEO-friendly websites and
              web applications for businesses, startups,
              organizations, and professional brands.
            </p>

          </article>


          {/* MOBILE */}

          <article className="service-mini-card">

            <span>
              📱
            </span>

            <h3>
              Mobile App Development
            </h3>

            <p>
              Android and iOS applications using modern
              mobile development technologies for consumer
              and business use cases.
            </p>

          </article>


          {/* AI */}

          <article className="service-mini-card">

            <span>
              🤖
            </span>

            <h3>
              AI Solutions
            </h3>

            <p>
              AI-powered applications, automation,
              chatbots, intelligent systems, and custom
              machine learning solutions.
            </p>

          </article>


          {/* ECOMMERCE */}

          <article className="service-mini-card">

            <span>
              🛒
            </span>

            <h3>
              Ecommerce Development
            </h3>

            <p>
              Ecommerce websites and digital commerce
              solutions designed to help businesses sell
              products and manage their online presence.
            </p>

          </article>


          {/* GAME */}

          <article className="service-mini-card">

            <span>
              🎮
            </span>

            <h3>
              Game Development
            </h3>

            <p>
              Interactive games and digital experiences
              using modern game development technologies.
            </p>

          </article>


          {/* TRAINING */}

          <article className="service-mini-card">

            <span>
              🎓
            </span>

            <h3>
              Technology Training
            </h3>

            <p>
              Practical courses, internships, mentorship,
              and project-based technology training for
              students and aspiring professionals.
            </p>

          </article>

        </div>

      </section>


      {/* =====================================================
          CTA
      ====================================================== */}

      <section className="offer-cta">

        <div className="cta-content">

          <span className="cta-eyebrow">
            LET'S WORK TOGETHER
          </span>

          <h2>
            Let’s Build the Future Together 🚀
          </h2>

          <p>
            Whether you need a{" "}
            <strong>
              website, mobile application, ecommerce
              platform, game, AI solution, or custom
              software
            </strong>
            , SamSoftix is ready to help turn your idea into
            a practical digital solution.
          </p>

          <div className="cta-buttons">

            <button
              className="cta-btn"
              onClick={handleContactClick}
            >
              Contact Us Today
            </button>

            <button
              className="cta-secondary-btn"
              onClick={handleInternsClick}
            >
              Explore Training & Internships
            </button>

          </div>

        </div>

      </section>

    </div>
  );
}

export default About;